import React, { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import { ReactComponent as LoveIcon } from '../../../assets/images/project/Icon-love-grey.svg';
import { TOGGLE_LIKE } from '../../../graphql/TOGGLE_LIKE';
import { TOGGLE_UPDATE_LIKE } from '../../../graphql/TOGGLE_UPDATE_LIKE';
import { GET_CURRENT_USER_me } from '../../../graphql/types/GET_CURRENT_USER';
import { GET_PROJECT_getProject_likes, GET_PROJECT_getProject_updates_likes } from '../../../graphql/types/GET_PROJECT';
import { useRedirect } from '../../../hooks/useRedirect';

enum EPageTypes {
  list = 'list',
  project = 'project',
  projectUpdate = 'projectUpdate'
}

interface ILikeProject {
  postId: string;
  likes: GET_PROJECT_getProject_likes[] | GET_PROJECT_getProject_updates_likes[];
  wasLikedByUser: boolean;
  pageType?: string;
  user?: GET_CURRENT_USER_me;
}

const MAX_USERS_TO_DISPLAY = 9;

const LikeProject: React.FunctionComponent<ILikeProject> = ({
  postId,
  likes,
  wasLikedByUser,
  pageType,
  user
}: ILikeProject) => {
  const [fakeLike, setFakeLike] = useState({
    likesLength: likes.length,
    wasLiked: wasLikedByUser
  });

  const { redirectToHomePage } = useRedirect();

  useEffect(() => {
    setFakeLike({
      likesLength: likes.length,
      wasLiked: wasLikedByUser
    });
  }, [likes.length, wasLikedByUser, postId]);

  const handleFakeLike = () => {
    setFakeLike((prevLike) => {
      const likesLength = prevLike.wasLiked
        ? Math.max(0, prevLike.likesLength - 1)
        : prevLike.likesLength + 1;

      return {
        ...prevLike,
        likesLength,
        wasLiked: !prevLike.wasLiked
      };
    });
  };

  const [toggleLike] = useMutation(TOGGLE_LIKE, {
    variables: {
      projectId: postId
    }
  });

  const [toggleProjectUpdateLike] = useMutation(TOGGLE_UPDATE_LIKE, {
    variables: {
      updateId: postId
    }
  });

  const hasMoreLikes = likes.length > MAX_USERS_TO_DISPLAY;

  const handleLike = async () => {
    if (!user) {
      redirectToHomePage();
      return;
    }

    handleFakeLike();

    try {
      if (pageType === 'project') {
        await toggleLike();
      } else if (pageType === 'projectUpdate') {
        await toggleProjectUpdateLike();
      } else {
        handleFakeLike();
        toast.error('There is something wrong happen when you like ...');
      }
    } catch (error) {
      handleFakeLike();
      toast.error('Failed to update like. Please try again.');
    }
  };

  return (
    <button
      type="button"
      className={`post-card-action-button post-card-action-button--like tooltip${
        fakeLike.wasLiked ? ' is-liked' : ''
      }`}
      onClick={handleLike}
      disabled={!user}
      aria-pressed={fakeLike.wasLiked}
      aria-label={fakeLike.wasLiked ? 'Unlike project' : 'Like project'}
    >
      <LoveIcon
        className="post-card-action-icon"
        fill={fakeLike.wasLiked ? '#5775FF' : 'none'}
        stroke={fakeLike.wasLiked ? '#5775FF' : '#5D5D5D'}
      />
      <span className="post-card-action-count">{fakeLike.likesLength}</span>
      {likes.length > 0 && (
        <div className="tooltiptext is-size-8">
          {likes.map((like: GET_PROJECT_getProject_likes | GET_PROJECT_getProject_updates_likes, idx: number) => {
            if (idx < MAX_USERS_TO_DISPLAY && like?.author?.profile?.name) {
              return (
                <span className="is-block" key={'id' in like ? like.id : `like-${idx}`}>
                  {like.author.profile.name}
                </span>
              );
            }
            return undefined;
          })}
          {hasMoreLikes && (
            <span className="is-block" key={`more-${likes.length}`}>
              {`and ${likes.length - MAX_USERS_TO_DISPLAY} more...`}
            </span>
          )}
        </div>
      )}
    </button>
  );
};

LikeProject.defaultProps = {
  pageType: EPageTypes.list
};

export default LikeProject;
